import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Search,
  Download,
  Calendar,
  User,
  Clock,
  BookOpen,
  CreditCard,
  Eye,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";

interface AccountDeletionFeedback {
  id: number;
  user_id: number;
  auth_uid: string;
  email: string;
  name: string;
  reason: string;
  additional_feedback?: string;
  user_agent?: string;
  ip_address?: string;
  app_version?: string;
  platform?: string;
  subscription_status?: string;
  last_login_at?: string;
  account_age_days?: number;
  total_books_read?: number;
  total_reading_time_minutes?: number;
  deletion_requested_at: string;
  created_at: string;
}

const AccountDeletionFeedback = () => {
  const [feedbacks, setFeedbacks] = useState<AccountDeletionFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [selectedFeedback, setSelectedFeedback] =
    useState<AccountDeletionFeedback | null>(null);
  const { toast } = useToast();

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);

      let query = (supabase as any)
        .from("account_deletion_feedbacks")
        .select("*")
        .order("created_at", { ascending: false });

      if (searchTerm) {
        query = query.or(
          `email.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`
        );
      }

      if (reasonFilter !== "all") {
        query = query.eq("reason", reasonFilter);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setFeedbacks(data || []);
    } catch (error) {
      console.error("Error fetching account deletion feedbacks:", error);
      toast({
        title: "Error",
        description: "Failed to fetch account deletion feedbacks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [searchTerm, reasonFilter]);

  const exportFeedbacks = async () => {
    try {
      const csvContent = [
        // CSV headers
        [
          "ID",
          "User ID",
          "Email",
          "Name",
          "Reason",
          "Additional Feedback",
          "Platform",
          "Subscription Status",
          "Account Age (Days)",
          "Total Books Read",
          "Total Reading Time (Minutes)",
          "Last Login",
          "Deletion Requested At",
          "Created At",
        ].join(","),
        // CSV data
        ...feedbacks.map((feedback) =>
          [
            feedback.id,
            feedback.user_id,
            `"${feedback.email}"`,
            `"${feedback.name}"`,
            `"${feedback.reason}"`,
            `"${feedback.additional_feedback || ""}"`,
            feedback.platform || "",
            feedback.subscription_status || "",
            feedback.account_age_days || 0,
            feedback.total_books_read || 0,
            feedback.total_reading_time_minutes || 0,
            feedback.last_login_at
              ? format(new Date(feedback.last_login_at), "yyyy-MM-dd HH:mm:ss")
              : "",
            format(
              new Date(feedback.deletion_requested_at),
              "yyyy-MM-dd HH:mm:ss"
            ),
            format(new Date(feedback.created_at), "yyyy-MM-dd HH:mm:ss"),
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `account-deletion-feedbacks-${format(
        new Date(),
        "yyyy-MM-dd"
      )}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Feedbacks exported successfully",
      });
    } catch (error) {
      console.error("Error exporting feedbacks:", error);
      toast({
        title: "Error",
        description: "Failed to export feedbacks",
        variant: "destructive",
      });
    }
  };

  const getReasonBadgeColor = (reason: string) => {
    const colorMap: Record<string, string> = {
      privacy_concerns: "bg-red-100 text-red-800",
      not_useful: "bg-orange-100 text-orange-800",
      too_expensive: "bg-yellow-100 text-yellow-800",
      technical_issues: "bg-purple-100 text-purple-800",
      switching_platforms: "bg-blue-100 text-blue-800",
      temporary_break: "bg-green-100 text-green-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colorMap[reason] || "bg-gray-100 text-gray-800";
  };

  const formatReasonText = (reason: string) => {
    const reasonMap: Record<string, string> = {
      privacy_concerns: "Privacy Concerns",
      not_useful: "Not Useful",
      too_expensive: "Too Expensive",
      technical_issues: "Technical Issues",
      switching_platforms: "Switching Platforms",
      temporary_break: "Temporary Break",
      other: "Other",
    };
    return reasonMap[reason] || reason;
  };

  const uniqueReasons = Array.from(new Set(feedbacks.map((f) => f.reason)));

  return (
    <AdminLayout title="Account Deletion Feedback">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground">
              View and analyze feedback from users who deleted their accounts
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchFeedbacks} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportFeedbacks} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Total Deletions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{feedbacks.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {
                  feedbacks.filter(
                    (f) =>
                      new Date(f.created_at).getMonth() ===
                        new Date().getMonth() &&
                      new Date(f.created_at).getFullYear() ===
                        new Date().getFullYear()
                  ).length
                }
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top Reason</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">
                {uniqueReasons.length > 0
                  ? formatReasonText(
                      uniqueReasons.reduce((a, b) =>
                        feedbacks.filter((f) => f.reason === a).length >
                        feedbacks.filter((f) => f.reason === b).length
                          ? a
                          : b
                      )
                    )
                  : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Account Age
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {feedbacks.length > 0
                  ? Math.round(
                      feedbacks.reduce(
                        (sum, f) => sum + (f.account_age_days || 0),
                        0
                      ) / feedbacks.length
                    )
                  : 0}
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  days
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="search">Search by email or name</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Label htmlFor="reason-filter">Filter by reason</Label>
                <Select value={reasonFilter} onValueChange={setReasonFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All reasons" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All reasons</SelectItem>
                    {uniqueReasons.map((reason) => (
                      <SelectItem key={reason} value={reason}>
                        {formatReasonText(reason)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedbacks Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Account Deletion Feedbacks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Account Age</TableHead>
                    <TableHead>Usage Stats</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Deleted At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{feedback.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {feedback.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getReasonBadgeColor(feedback.reason)}>
                          {formatReasonText(feedback.reason)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {feedback.account_age_days || 0} days
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <BookOpen className="w-3 h-3" />
                            {feedback.total_books_read || 0} books
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {Math.round(
                              (feedback.total_reading_time_minutes || 0) / 60
                            )}
                            h read
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{feedback.platform || "Unknown"}</div>
                          {feedback.subscription_status && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <CreditCard className="w-3 h-3" />
                              {feedback.subscription_status}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(
                          new Date(feedback.deletion_requested_at),
                          "MMM dd, yyyy"
                        )}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedFeedback(feedback)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>
                                Account Deletion Details
                              </DialogTitle>
                              <DialogDescription>
                                Detailed feedback from {selectedFeedback?.name}
                              </DialogDescription>
                            </DialogHeader>
                            {selectedFeedback && (
                              <ScrollArea className="max-h-[60vh]">
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-sm font-medium">
                                        User Information
                                      </Label>
                                      <div className="mt-1 space-y-1">
                                        <div className="flex items-center gap-2">
                                          <User className="w-4 h-4" />
                                          {selectedFeedback.name} (
                                          {selectedFeedback.email})
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                          User ID: {selectedFeedback.user_id}
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Deletion Reason
                                      </Label>
                                      <div className="mt-1">
                                        <Badge
                                          className={getReasonBadgeColor(
                                            selectedFeedback.reason
                                          )}
                                        >
                                          {formatReasonText(
                                            selectedFeedback.reason
                                          )}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>

                                  <Separator />

                                  <div>
                                    <Label className="text-sm font-medium">
                                      Additional Feedback
                                    </Label>
                                    <div className="mt-1 p-3 bg-muted rounded-md">
                                      {selectedFeedback.additional_feedback ||
                                        "No additional feedback provided"}
                                    </div>
                                  </div>

                                  <Separator />

                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Account Age
                                      </Label>
                                      <div className="mt-1 flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        {selectedFeedback.account_age_days ||
                                          0}{" "}
                                        days
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Books Read
                                      </Label>
                                      <div className="mt-1 flex items-center gap-1">
                                        <BookOpen className="w-4 h-4" />
                                        {selectedFeedback.total_books_read || 0}
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Reading Time
                                      </Label>
                                      <div className="mt-1 flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {Math.round(
                                          (selectedFeedback.total_reading_time_minutes ||
                                            0) / 60
                                        )}
                                        h
                                      </div>
                                    </div>
                                  </div>

                                  <Separator />

                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Platform
                                      </Label>
                                      <div className="mt-1">
                                        {selectedFeedback.platform || "Unknown"}
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Subscription Status
                                      </Label>
                                      <div className="mt-1">
                                        {selectedFeedback.subscription_status ||
                                          "None"}
                                      </div>
                                    </div>
                                  </div>

                                  <Separator />

                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Last Login
                                      </Label>
                                      <div className="mt-1">
                                        {selectedFeedback.last_login_at
                                          ? format(
                                              new Date(
                                                selectedFeedback.last_login_at
                                              ),
                                              "MMM dd, yyyy HH:mm"
                                            )
                                          : "Never"}
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-sm font-medium">
                                        Deletion Date
                                      </Label>
                                      <div className="mt-1">
                                        {format(
                                          new Date(
                                            selectedFeedback.deletion_requested_at
                                          ),
                                          "MMM dd, yyyy HH:mm"
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {selectedFeedback.user_agent && (
                                    <>
                                      <Separator />
                                      <div>
                                        <Label className="text-sm font-medium">
                                          Technical Details
                                        </Label>
                                        <div className="mt-1 space-y-1 text-sm text-muted-foreground">
                                          <div>
                                            User Agent:{" "}
                                            {selectedFeedback.user_agent}
                                          </div>
                                          {selectedFeedback.ip_address && (
                                            <div>
                                              IP Address:{" "}
                                              {selectedFeedback.ip_address}
                                            </div>
                                          )}
                                          {selectedFeedback.app_version && (
                                            <div>
                                              App Version:{" "}
                                              {selectedFeedback.app_version}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </ScrollArea>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!loading && feedbacks.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No account deletion feedbacks found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AccountDeletionFeedback;
